import { CareOSLoader } from '@/components/ui/careos-loader';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { track } from '@/lib/analytics';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AIWizardUpload } from '@/components/care-plan/AIWizardUpload';
import { AISuggestionReview } from '@/components/care-plan/AISuggestionReview';
import {
  Sparkles,
  FileText,
  Camera,
  Loader2,
  AlertTriangle,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { usePatientRole } from '@/hooks/usePatientRole';
import { isPatientInactiveRlsError, PATIENT_INACTIVE_TOAST_MESSAGE } from '@/lib/patient-status-errors';

export interface MedicationSuggestion {
  name: string;
  dose: string;
  dose_unit: string;
  administration_route: string;
  schedule_times: string[];
  is_sporadic: boolean;
  notes?: string;
  accepted?: boolean;
}

export interface RoutineSuggestion {
  type: 'vital_sign' | 'hygiene' | 'food' | 'physical_activity' | 'other';
  name: string;
  schedule_times: string[];
  vital_type?: string;
  hygiene_type?: string;
  food_type?: string;
  
  accepted?: boolean;
}

export interface CarePlanSuggestions {
  medications: MedicationSuggestion[];
  dietary_restrictions: string[];
  mobility_needs: 'independent' | 'needs_support' | 'wheelchair' | 'bedridden';
  suggested_routines: RoutineSuggestion[];
  additional_notes: string;
}

type WizardStep = 'input' | 'review' | 'saving';

export default function AIWizardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: patientId } = useParams<{ id: string }>();
  const { user } = useAuth();

  const { isPatientActive, isPatientSuspended, isPatientArchived } = usePatientRole(patientId);
  const [step, setStep] = useState<WizardStep>('input');
  const [inputMode, setInputMode] = useState<'text' | 'image'>('text');
  const [textDescription, setTextDescription] = useState('');
  const [selectedImages, setSelectedImages] = useState<{ base64: string; mimeType: string }[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [suggestions, setSuggestions] = useState<CarePlanSuggestions | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleImagesChange = (images: { base64: string, mimeType: string }[]) => {
    setSelectedImages(images);
    setError(null);
  };

  const handleAnalyze = async () => {
    if (!isPatientActive) {
      toast.error(PATIENT_INACTIVE_TOAST_MESSAGE);
      return;
    }
    if (inputMode === 'text' && !textDescription.trim()) {
      toast.error(t('aiWizard.errorNoInput'));
      return;
    }
    if (inputMode === 'image' && selectedImages.length === 0) {
      toast.error(t('aiWizard.errorNoImage'));
      return;
    }

    // Payload size safeguard (approximate base64 size)
    const totalSize = selectedImages.reduce((sum, img) => sum + img.base64.length, 0);
    // Limit to ~12MB for base64 payload (~9MB actual data)
    // Most Edge Functions have a 10MB limit.
    if (inputMode === 'image' && totalSize > 12 * 1024 * 1024) {
      toast.error(t('aiWizard.errorPayloadTooLarge') || 'O conjunto de imagens é muito pesado para processar de uma vez. Tente remover algumas ou enviar em menor resolução.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      console.log(`Calling parse-care-plan with ${inputMode} mode. Image count: ${selectedImages.length}`);

      const { data, error: fnError } = await supabase.functions.invoke('parse-care-plan', {
        body: {
          textDescription: inputMode === 'text' ? textDescription : null,
          images: inputMode === 'image' ? selectedImages : null,
        }
      });

      if (fnError) {
        console.error('Functions error:', fnError);
        throw fnError;
      }

      if (data.error) {
        console.error('Edge Function returned error:', data.error);
        setError(data.error);
        return;
      }

      if (data.suggestions) {
        // Mark all as accepted by default
        const enrichedSuggestions: CarePlanSuggestions = {
          ...data.suggestions,
          medications: data.suggestions.medications.map((m: MedicationSuggestion) => ({ ...m, accepted: true })),
          suggested_routines: data.suggestions.suggested_routines.map((r: RoutineSuggestion) => ({ ...r, accepted: true })),
        };
        setSuggestions(enrichedSuggestions);
        setStep('review');
      } else if (data.rawContent) {
        console.warn('AI returned raw content instead of structured suggestions');
        setError(t('aiWizard.errorParsing') + ': ' + data.rawContent);
      } else {
        throw new Error('No suggestions returned');
      }
    } catch (err: any) {
      console.error('Full analysis error:', err);
      setError(err.message || t('aiWizard.errorGeneric'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSaveSuggestions = async (action: 'add' | 'replace') => {
    if (!suggestions || !patientId || !user) return;

    setStep('saving');

    try {
      if (action === 'replace') {
        const { error: deleteError } = await supabase
          .from('care_plan_tasks')
          .update({ is_active: false })
          .eq('patient_id', patientId);

        if (deleteError) {
          console.error('Error deleting existing tasks:', deleteError);
          throw deleteError;
        }
      }

      // Save accepted medications
      const acceptedMeds = suggestions.medications.filter(m => m.accepted);
      for (const med of acceptedMeds) {
        const { error } = await supabase.from('care_plan_tasks').insert({
          patient_id: patientId,
          created_by: user.id,
          task_type: med.is_sporadic ? 'sporadic_medication' : 'fixed_medication',
          name: med.name,
          dose: med.dose,
          dose_unit: med.dose_unit,
          administration_route: med.administration_route,
          schedule_times: med.schedule_times,
          is_sporadic: med.is_sporadic,
          description: med.notes,
        });
        if (error) console.error('Error saving medication:', error);
      }

      // Save accepted routines
      const acceptedRoutines = suggestions.suggested_routines.filter(r => r.accepted);
      for (const routine of acceptedRoutines) {
        const taskData: any = {
          patient_id: patientId,
          created_by: user.id,
          task_type: routine.type,
          name: routine.name,
          schedule_times: routine.schedule_times,
        };

        if (routine.vital_type) taskData.vital_type = routine.vital_type;
        if (routine.hygiene_type) taskData.hygiene_type = routine.hygiene_type;
        if (routine.food_type) taskData.food_type = routine.food_type;

        const { error } = await supabase.from('care_plan_tasks').insert(taskData);
        if (error) console.error('Error saving routine:', error);
      }

      // `care_plan_created` comes from the server trigger; this event only adds
      // the AI-specific detail so the two do not double count.
      track('care_plan_ai_generated', {
        patient_id: patientId,
        created_via: 'ai_wizard',
        task_count: acceptedMeds.length + acceptedRoutines.length,
        action,
      });

      toast.success(t('aiWizard.savedSuccess'));
      navigate(`/app/patients/${patientId}/care-plan`);
    } catch (err: any) {
      console.error('Error saving:', err);
      if (isPatientInactiveRlsError(err)) {
        toast.error(PATIENT_INACTIVE_TOAST_MESSAGE);
      } else {
        toast.error(err.message || t('aiWizard.errorSaving'));
      }
      setStep('review');
    }
  };

  const handleUpdateSuggestions = (updated: CarePlanSuggestions) => {
    setSuggestions(updated);
  };

  return (
    <AppLayout
      title={t('aiWizard.title')}
      showBack
      onBack={() => navigate(`/app/patients/${patientId}/care-plan`)}
      showFAB={false}
    >
      <div className="p-4 pb-24">
        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step === 'input' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
            }`}>
            1
          </div>
          <div className="w-8 h-0.5 bg-muted" />
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step === 'review' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
            }`}>
            2
          </div>
          <div className="w-8 h-0.5 bg-muted" />
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step === 'saving' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
            }`}>
            3
          </div>
        </div>

        {/* Warning Banner */}
        <Alert className="mb-4 border-warning bg-warning/10">
          <AlertTriangle className="h-4 w-4 text-warning" />
          <AlertDescription className="text-sm">
            {t('aiWizard.disclaimer')}
          </AlertDescription>
        </Alert>

        {step === 'input' && (
          <>
            {/* Input Mode Tabs */}
            <Tabs
              value={inputMode}
              onValueChange={(v) => {
                setInputMode(v as 'text' | 'image');
                setError(null);
                // Clear state when switching to ensure a clean start
                if (v === 'text') setSelectedImages([]);
                if (v === 'image') setTextDescription('');
              }}
              className="mb-4"
            >
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="text" className="gap-2">
                  <FileText className="w-4 h-4" />
                  {t('aiWizard.textMode')}
                </TabsTrigger>
                <TabsTrigger value="image" className="gap-2">
                  <Camera className="w-4 h-4" />
                  {t('aiWizard.imageMode')}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="text" className="mt-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">{t('aiWizard.describePatient')}</CardTitle>
                    <CardDescription className="text-xs">
                      {t('aiWizard.describePatientHint')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      placeholder={t('aiWizard.textPlaceholder')}
                      value={textDescription}
                      onChange={(e) => setTextDescription(e.target.value)}
                      rows={8}
                      className="resize-none"
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="image" className="mt-4">
                <AIWizardUpload
                  onImagesChange={handleImagesChange}
                  currentImages={selectedImages}
                />
              </TabsContent>
            </Tabs>

            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button
              className="w-full gap-2"
              size="lg"
              onClick={handleAnalyze}
              disabled={isProcessing || (inputMode === 'text' && !textDescription.trim()) || (inputMode === 'image' && selectedImages.length === 0)}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('aiWizard.analyzing')}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  {t('aiWizard.analyzeButton')}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </>
        )}

        {step === 'review' && suggestions && (
          <AISuggestionReview
            suggestions={suggestions}
            onUpdate={handleUpdateSuggestions}
            onSave={handleSaveSuggestions}
            onBack={() => setStep('input')}
          />
        )}

        {step === 'saving' && (
          <div className="flex flex-col items-center justify-center py-12">
            <CareOSLoader size="lg" label={t('aiWizard.saving')} />
          </div>
        )}
      </div>
    </AppLayout>
  );
}
